/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import { Input, ModalConfirm, Spacer, theme } from "@nordcloud/gnui";
import { SearchPresetV2 } from "~/generated/graphql";
import { ListLoader, LoaderWrap } from "~/components";
import { generateActionConfirmText } from "~/tools";
import { DeleteItem, useRemoveSearchPreset } from "../hooks";
import { SearchPresetList } from "./SearchPresetList";

type Props = {
  onClick: (searchPreset: SearchPresetV2) => void;
  isLoading: boolean;
  presets: SearchPresetV2[];
  nameQuery: string;
  setNameQuery: (v: string) => void;
};

export function SearchPresets({
  onClick,
  isLoading,
  presets,
  nameQuery,
  setNameQuery,
}: Props) {
  const [deleteItem, setDeleteItem] = React.useState<DeleteItem | null>(null);

  const removeSearchPreset = useRemoveSearchPreset({
    deleteItem,
    onSuccess: () => setDeleteItem(null),
  });

  const showRemoveModal = deleteItem != null;

  return (
    <>
      <Input
        name="nameQuery"
        value={nameQuery}
        placeholder="Search"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setNameQuery(event.target.value)
        }
      />
      <Spacer height={theme.spacing.spacing04} />
      <LoaderWrap loading={isLoading} Component={ListLoader}>
        <SearchPresetList
          items={presets}
          onDelete={setDeleteItem}
          onClick={onClick}
        />
      </LoaderWrap>
      <ModalConfirm
        isOpen={showRemoveModal}
        confirm={removeSearchPreset}
        actionLabel="Remove"
        contentLabel="Remove Predefined Filter"
        onClose={() => setDeleteItem(null)}
      >
        {generateActionConfirmText(deleteItem?.name ?? "Predefined Filter")(
          "remove"
        )()}
      </ModalConfirm>
    </>
  );
}
